import { resolveTitle, valueFromSource } from "../../../../Helper/DigitalTwin/sourceFields";

/**
 * Pure helpers for the configurable Digital Twin header: the digitalTwin (or IOTDigitalTwin)
 * page meta's metaInfo.header, which an org sets at orgMeta.metaInfo.digitalTwin.header.
 *
 * header = {
 *   title: {
 *     icon, statusMarker,                       // CustomIcon name; operational status dot on the icon (default true)
 *     primaryDetails: { label, key, source, fallback: { label, key, source }, clickable, copyable, editable },
 *   },
 *   subHeader: ["signalStrength", "pingStatus", "location"]   // strings, as before, or objects:
 *            | [{ type: "signalStrength", source: "iotState", key: "ext_other_SIG_QUAL", max: 5 },
 *               { type: "pingStatus" }, { type: "location", map: true }],
 *   badges: ["soc", "activeAlerts", "bmsFaults"],
 *   metrics: [{ key, label, unit, source }],     // header right side, in this order; the first four show.
 *                                                // source: omitted = the real-time-matrix API (its
 *                                                // realTimeMatrixes keys, value already formatted, so
 *                                                // unit is not reapplied), or "vehicleInfo".
 *   aboutPopup: { enabled, fields: [{ label, key, source, copyable }] },
 * }
 *
 * For the title, About popup and signal strength, source is "batteryInfo" (real-time-info, the
 * default), "vehicleInfo" (/api/vehicle/<id>) or "iotState" (/api/iot/<id>/state, keys
 * ext_<signalType>_<attributeName>).
 */

/** The IoT state key samsar has always drawn as signal bars: SIG_QUAL of signal type other. */
// Field reading lives in Helper so the Vitals ID box can share it; re-exported for callers here.
export { resolveTitle, valueFromSource };

export const DEFAULT_SIGNAL_KEY = "ext_other_SIG_QUAL";
export const DEFAULT_SIGNAL_MAX = 5;

const SUB_HEADER_TYPES = ["signalStrength", "pingStatus", "location"];

/**
 * subHeader entries as objects ({ type, ... }), dropping unknown types.
 *
 * Compatibility: when every entry is a plain string (org meta written before object entries
 * existed), the items keep the fixed order they always had (signal strength, ping status,
 * location) whatever the array order, and legacy is true. With any object entry, the
 * configured order is used.
 */
export const subHeaderLayout = (subHeader) => {
    const list = Array.isArray(subHeader) ? subHeader : [];
    const legacy = list.every((item) => typeof item === "string");
    const items = list
        .map((item) => (typeof item === "string" ? { type: item } : item))
        .filter((item) => item && SUB_HEADER_TYPES.includes(item.type));
    if (legacy) items.sort((a, b) => SUB_HEADER_TYPES.indexOf(a.type) - SUB_HEADER_TYPES.indexOf(b.type));
    return { items, legacy };
};

/** subHeader entries as objects, in display order (see subHeaderLayout). */
export const normalizeSubHeader = (subHeader) => subHeaderLayout(subHeader).items;

/** Signal bars (0..5) for a signalStrength entry, scaled from its max. */
export const signalStrengthBars = (item, sources) => {
    const raw = parseFloat(valueFromSource(sources, { source: item?.source || "iotState", key: item?.key || DEFAULT_SIGNAL_KEY }));
    if (isNaN(raw) || raw <= 0) return 0;
    const max = parseFloat(item?.max) > 0 ? parseFloat(item.max) : DEFAULT_SIGNAL_MAX;
    return Math.max(0, Math.min(5, Math.round((raw / max) * 5)));
};

/** Whether any header part reads the IoT state (so the header fetches it). */
export const headerNeedsIotState = (headerConfig) => {
    const pd = headerConfig?.title?.primaryDetails;
    const reads = (f) => f?.source === "iotState";
    return normalizeSubHeader(headerConfig?.subHeader).some((i) => i.type === "signalStrength")
        || reads(pd) || reads(pd?.fallback)
        || (headerConfig?.aboutPopup?.fields || []).some(reads);
};

/**
 * Header metrics in the configured order. metricList is the real-time-matrix list
 * ([{ key, label, unit, value }], in the API's key order); config is header.metrics.
 * vehicleInfo entries take their value from the vehicle (formatted by formatValue); any
 * other source is the API's. Metrics not in config are left out, also from the pencil.
 * Without config the API list is returned as is.
 */
export const orderHeaderMetrics = (metricList, config, sources, formatValue = (v) => v) => {
    if (!Array.isArray(config) || !config.length) return metricList || [];
    const byKey = Object.fromEntries((metricList || []).map((m) => [m.key, m]));
    return config.filter((c) => c && c.key).map((c) => {
        if (c.source === "vehicleInfo") {
            const raw = valueFromSource(sources, c);
            return { key: c.key, label: c.label || c.key, unit: c.unit || "", value: raw === null ? "NA" : formatValue({ value: String(raw), unit: c.unit || "" }, c.key) };
        }
        const fromApi = byKey[c.key];
        return {
            ...(fromApi || { key: c.key, value: "NA" }),
            label: c.label || fromApi?.label || c.key,
            ...(c.unit !== undefined ? { unit: c.unit } : {}),
        };
    });
};

/** Device Health: the SIG_QUAL attribute, whatever its signal type prefix (ext_<type>_SIG_QUAL). */
export const isSignalQualityAttribute = (attributeName) =>
    typeof attributeName === "string" && /^ext_(?:[A-Za-z0-9]+_)?SIG_QUAL$/.test(attributeName);
